import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { apiJson } from '../../apiClient';
import { downloadExpensesCsv, downloadExpensesPdf } from '../../utils/expenseExport';
import { expenseSummaryKeysForUser } from '../../constants/factoryExpenseCategories';
import { useLanguage } from '../../i18n/LanguageContext';
import { isBrickFactoryUser } from '../../utils/businessType';
import { consumeExpensesSummaryPrefs } from '../../utils/navigationFocus';
import ExpensesTrendChart from '../../components/ExpensesTrendChart';
import FactoryExpensesHub from '../../components/FactoryExpensesHub';
import './ExpensesShell.css';

function formatRangeLabel(from, to, window, lang) {
  if (!from || !to) return '';
  try {
    const locale = lang === 'sw' ? 'sw-TZ' : undefined;
    const a = new Date(`${from}T12:00:00`);
    const b = new Date(`${to}T12:00:00`);
    if (window === 'day') {
      return a.toLocaleDateString(locale, { dateStyle: 'long' });
    }
    const opts = { month: 'short', day: 'numeric', year: a.getFullYear() !== b.getFullYear() ? 'numeric' : undefined };
    return `${a.toLocaleDateString(locale, opts)} – ${b.toLocaleDateString(locale, { dateStyle: 'medium' })}`;
  } catch {
    return `${from} → ${to}`;
  }
}

const ExpensesSummary = ({ user, onNavigate }) => {
  const { t, lang } = useLanguage();
  const factory = isBrickFactoryUser(user);
  const summaryKeys = useMemo(() => expenseSummaryKeysForUser(user, lang), [user, lang]);
  const [windowKey, setWindowKey] = useState('week');
  const [anchorDate, setAnchorDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ window: windowKey, date: anchorDate });
      const data = await apiJson(`/expenses/summary?${qs.toString()}`);
      setPayload({
        window: data.window,
        from: data.from,
        to: data.to,
        buckets: data.buckets || { boss: 0, staff: 0, office: 0 },
        series: Array.isArray(data.series) ? data.series : [],
      });
    } catch (e) {
      setPayload(null);
      setError(e.message || t('exp.common.loadSummaryError'));
    } finally {
      setLoading(false);
    }
  }, [windowKey, anchorDate, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useLayoutEffect(() => {
    const p = consumeExpensesSummaryPrefs();
    if (!p) return;
    if (p.window === 'day' || p.window === 'week' || p.window === 'month') setWindowKey(p.window);
    if (typeof p.anchorDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(p.anchorDate)) setAnchorDate(p.anchorDate);
  }, []);

  const go = (pageKey, path) => {
    if (typeof onNavigate === 'function') {
      onNavigate(pageKey);
    } else if (typeof window !== 'undefined') {
      window.location.assign(path);
    }
  };

  const rangeTitle = payload ? formatRangeLabel(payload.from, payload.to, windowKey, lang) : '';

  const fetchPeriodExpenses = async () => {
    if (!payload?.from || !payload?.to) return [];
    const data = await apiJson('/expenses');
    const all = Array.isArray(data.expenses) ? data.expenses : [];
    return all.filter((e) => {
      const d = e.expenseDate;
      return d && d >= payload.from && d <= payload.to;
    });
  };

  const periodExportOptions = () => ({
    title: t('exp.summary.title'),
    subtitle: rangeTitle,
    criteria: [`Period: ${payload?.from} → ${payload?.to}`],
    includeCategory: true,
    lang,
  });

  const downloadPeriodCsv = async () => {
    if (!payload?.from || !payload?.to) return;
    setExportingCsv(true);
    try {
      const filtered = await fetchPeriodExpenses();
      downloadExpensesCsv(filtered, user, {
        ...periodExportOptions(),
        filename: `expenses_${payload.from}_${payload.to}.csv`,
      });
    } catch (e) {
      window.alert(e.message || t('exp.common.exportError'));
    } finally {
      setExportingCsv(false);
    }
  };

  const downloadPeriodPdf = async () => {
    if (!payload?.from || !payload?.to || exportingPdf) return;
    setExportingPdf(true);
    try {
      const filtered = await fetchPeriodExpenses();
      await downloadExpensesPdf(filtered, user, periodExportOptions());
    } catch (e) {
      window.alert(e.message || t('exp.list.pdfError'));
    } finally {
      setExportingPdf(false);
    }
  };

  const statIcons = { boss: '👤', staff: '👥', office: '🏢' };

  return (
    <div className="expenses-summary-shell expenses-page page-bleed">
      <header className={factory ? 'expenses-overview-hero' : 'expenses-header'}>
        {factory ? (
          <>
            <div className="expenses-overview-hero__text">
              <p className="expenses-overview-hero__eyebrow">{t('exp.summary.eyebrow')}</p>
              <h1 className="expenses-overview-hero__title">{t('exp.summary.overview')}</h1>
              <p className="expenses-overview-hero__lede">{t('exp.summary.ledeFactory')}</p>
            </div>
            <div className="expenses-overview-hero__actions">
              <button type="button" className="expenses-overview-hero__btn" onClick={() => go('expenses-add')}>
                {t('exp.summary.recordCost')}
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="expenses-title">{t('exp.summary.title')}</h1>
            <p className="expenses-lede">{t('exp.summary.ledeRetail')}</p>
          </>
        )}
      </header>

      {factory ? <FactoryExpensesHub user={user} onNavigate={onNavigate} /> : null}

      <div className="expenses-page__body">
      <div className="expenses-card expenses-card--filters">
        <div className="expenses-filter-row">
          <span className="expenses-filter-label">{t('exp.period.label')}</span>
          <div className="expenses-segments" role="group" aria-label={t('exp.period.label')}>
            {[
              { k: 'day', l: t('exp.period.day') },
              { k: 'week', l: t('exp.period.week') },
              { k: 'month', l: t('exp.period.month') },
            ].map(({ k, l }) => (
              <button
                key={k}
                type="button"
                className={`expenses-segment${windowKey === k ? ' is-active' : ''}`}
                onClick={() => setWindowKey(k)}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <label className="expenses-filter-date">
          <span className="expenses-filter-label">{t('exp.period.anchor')}</span>
          <input type="date" value={anchorDate} onChange={(e) => setAnchorDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
        </label>
        <div className="expenses-toolbar expenses-toolbar--tight">
          <button
            type="button"
            className="expenses-btn expenses-btn--secondary"
            disabled={loading || exportingCsv || !payload}
            onClick={() => void downloadPeriodCsv()}
          >
            {exportingCsv ? t('exp.export.exportingCsv') : t('exp.export.csv')}
          </button>
          <button
            type="button"
            className="expenses-btn expenses-btn--secondary"
            disabled={loading || exportingPdf || !payload}
            onClick={() => void downloadPeriodPdf()}
          >
            {exportingPdf ? t('exp.export.exportingPdf') : t('exp.export.pdf')}
          </button>
          <button type="button" className="expenses-btn expenses-btn--secondary" onClick={() => void load()} disabled={loading}>
            {t('exp.common.refresh')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="expenses-card expenses-card--fill">
          <p className="expenses-empty-text">{t('exp.common.loading')}</p>
        </div>
      ) : error ? (
        <div className="expenses-card expenses-card--fill">
          <p className="expenses-inline-msg expenses-inline-msg--err" role="alert">
            {error}
          </p>
        </div>
      ) : payload ? (
        <>
          <p className="expenses-range-caption">{rangeTitle}</p>
          <div className="expenses-card expenses-card--stats">
            <h2 className="expenses-summary-section-title">{t('exp.totals.title')}</h2>
            {factory ? <p className="expenses-summary-hint">{t('exp.totals.hint')}</p> : null}
            <div className="expenses-summary-grid">
              {summaryKeys.map((meta) => {
                const amount = typeof payload.buckets[meta.key] === 'number' ? payload.buckets[meta.key] : 0;
                return (
                  <button
                    key={meta.key}
                    type="button"
                    className={`expenses-summary-stat expenses-summary-stat--${meta.key}`}
                    onClick={() => go(meta.pageKey, meta.path)}
                  >
                    <span className="expenses-summary-stat__head">
                      <span className="expenses-summary-stat-label">{meta.label}</span>
                      <span className="expenses-summary-stat-icon" aria-hidden>
                        {statIcons[meta.key] || '•'}
                      </span>
                    </span>
                    <span className="expenses-summary-stat-amount">{amount.toLocaleString()}</span>
                    <span className="expenses-summary-stat-currency">{t('exp.totals.tapList')}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="expenses-card expenses-card--chart expenses-card--fill">
            <h2 className="expenses-summary-section-title">{t('exp.trend.title')}</h2>
            <p className="expenses-chart-lede">{factory ? t('exp.trend.ledeFactory') : t('exp.trend.ledeRetail')}</p>
            <ExpensesTrendChart series={payload.series} factory={factory} />
          </div>
        </>
      ) : null}
      </div>
    </div>
  );
};

export default ExpensesSummary;
